import React, { useState, useEffect } from 'react';
import { Plus, Calendar, X, Check, XCircle, Users, Clock, Edit, CheckCircle, Trash2, Archive } from 'lucide-react';
import { holidayRequestApi, employeeApi, blockedDateApi, leaveTypeApi } from '../services/api';
import { HolidayRequest, Employee, CreateHolidayRequestRequest, UpdateHolidayRequestRequest, BlockedDate, CreateBlockedDateRequest, LeaveType } from '../types';

const HolidayAdmin: React.FC = () => {
  const [holidayRequests, setHolidayRequests] = useState<HolidayRequest[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [leaveTypes, setLeaveTypes] = useState<LeaveType[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<string | null>(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [formData, setFormData] = useState<CreateHolidayRequestRequest>({
    employeeId: 0,
    managerSupervisor: '',
    venueDepartment: '',
    startDate: '',
    endDate: '',
    reason: '',
    status: 'Pending',
  });
  const [saving, setSaving] = useState(false);
  const [openActionsId, setOpenActionsId] = useState<number | null>(null);
  const [actionComment, setActionComment] = useState<string>('');
  const [processingAction, setProcessingAction] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingRequestId, setEditingRequestId] = useState<number | null>(null);
  const [softDeletedIds, setSoftDeletedIds] = useState<number[]>([]);
  const [blockedDates, setBlockedDates] = useState<BlockedDate[]>([]);
  const [blockedDateForm, setBlockedDateForm] = useState<CreateBlockedDateRequest>({
    startDate: '',
    endDate: '',
    reason: '',
  });
  const [editingBlockedDate, setEditingBlockedDate] = useState<BlockedDate | null>(null);
  const [softDeletedBlockedIds, setSoftDeletedBlockedIds] = useState<number[]>([]);
  const [showBlockedDateForm, setShowBlockedDateForm] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [requests, emps, blocked, leaveTypesData] = await Promise.all([
        holidayRequestApi.getAll(),
        employeeApi.getAll(),
        blockedDateApi.getAll(),
        leaveTypeApi.getAll(),
      ]);
      setHolidayRequests(requests);
      setEmployees(emps);
      setBlockedDates(blocked);
      setLeaveTypes(leaveTypesData);
    } catch (error) {
      console.error('Error fetching data:', error);
      alert('Failed to load holiday requests. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      employeeId: 0,
      managerSupervisor: '',
      venueDepartment: '',
      startDate: '',
      endDate: '',
      leaveTypeId: undefined,
      isHalfDay: false,
      halfDayType: undefined,
      reason: '',
      status: 'Pending',
    });
    setIsEditing(false);
    setEditingRequestId(null);
  };

  const isRangeBlocked = (start: string, end: string) => {
    if (!start || !end) return false;
    const startDate = new Date(start + 'T00:00:00');
    const endDate = new Date(end + 'T00:00:00');

    return blockedDates.some(bd => {
      const bdStart = new Date(bd.startDate + 'T00:00:00');
      const bdEnd = new Date(bd.endDate + 'T00:00:00');

      return (
        (bdStart <= startDate && bdEnd >= startDate) ||
        (bdStart <= endDate && bdEnd >= endDate) ||
        (bdStart >= startDate && bdEnd <= endDate)
      );
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (saving) return;
    
    if (formData.employeeId === 0) {
      alert('Please select an employee.');
      return;
    }

    if (!formData.managerSupervisor || formData.managerSupervisor.trim() === '') {
      alert('Please enter a Manager/Supervisor.');
      return;
    }

    if (!formData.venueDepartment || formData.venueDepartment.trim() === '') {
      alert('Please enter a Venue/Department.');
      return;
    }

    if (!formData.startDate || !formData.endDate) {
      alert('Please select both start and end dates.');
      return;
    }

    if (isRangeBlocked(formData.startDate, formData.endDate)) {
      alert('Selected dates fall within blocked dates and cannot be requested.');
      return;
    }
    try {
      setSaving(true);

      if (isEditing && editingRequestId != null) {
        const updatePayload: UpdateHolidayRequestRequest = {
          managerSupervisor: formData.managerSupervisor,
          venueDepartment: formData.venueDepartment,
          startDate: formData.startDate,
          endDate: formData.endDate,
          leaveTypeId: formData.leaveTypeId,
          isHalfDay: formData.isHalfDay,
          halfDayType: formData.halfDayType,
          reason: formData.reason,
          status: formData.status || 'Pending',
        };

        await holidayRequestApi.update(editingRequestId, updatePayload);
      } else {
        await holidayRequestApi.create(formData);
      }

      setShowModal(false);
      resetForm();
      await fetchData();
    } catch (error: any) {
      console.error(isEditing ? 'Error updating holiday request:' : 'Error creating holiday request:', error);
      const errorMessage = error?.response?.data?.message || error?.message || `Failed to ${isEditing ? 'update' : 'create'} holiday request. Please try again.`;
      alert(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleApprove = async (id: number) => {
    if (window.confirm('Are you sure you want to approve this holiday request?')) {
      try {
        await holidayRequestApi.approve(id);
        await fetchData();
      } catch (error) {
        console.error('Error approving holiday request:', error);
        alert('Failed to approve holiday request. Please try again.');
      }
    }
  };

  const handleReject = async (id: number) => {
    const reason = window.prompt('Please provide a reason for rejection:');
    if (reason !== null) {
      try {
        await holidayRequestApi.reject(id, reason);
        await fetchData();
      } catch (error) {
        console.error('Error rejecting holiday request:', error);
        alert('Failed to reject holiday request. Please try again.');
      }
    }
  };

  const handleApproveWithComment = async (id: number) => {
    if (processingAction) return;
    
    try {
      setProcessingAction(true);
      await holidayRequestApi.approve(id);
      setOpenActionsId(null);
      setActionComment('');
      await fetchData();
    } catch (error) {
      console.error('Error approving holiday request:', error);
      alert('Failed to approve holiday request. Please try again.');
    } finally {
      setProcessingAction(false);
    }
  };

  const handleDenyWithComment = async (id: number) => {
    if (processingAction) return;
    
    if (!actionComment.trim()) {
      alert('Please provide a comment before denying the request.');
      return;
    }

    try {
      setProcessingAction(true);
      await holidayRequestApi.reject(id, actionComment);
      setOpenActionsId(null);
      setActionComment('');
      await fetchData();
    } catch (error) {
      console.error('Error denying holiday request:', error);
      alert('Failed to deny holiday request. Please try again.');
    } finally {
      setProcessingAction(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this holiday request?')) {
      try {
        await holidayRequestApi.delete(id);
        await fetchData();
      } catch (error) {
        console.error('Error deleting holiday request:', error);
        alert('Failed to delete holiday request. Please try again.');
      }
    }
  };

  // Calendar rendering
  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days: (Date | null)[] = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    
    return days;
  };

  const getHolidayRequestsForDate = (date: Date | null) => {
    if (!date) return [];
    // Normalize date to YYYY-MM-DD format for comparison
    const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    return holidayRequests.filter(hr => {
      const start = hr.startDate.split('T')[0]; // Get YYYY-MM-DD part
      const end = hr.endDate.split('T')[0]; // Get YYYY-MM-DD part
      return dateStr >= start && dateStr <= end;
    });
  };

  const getHolidayRequestsForDateAndStatus = (date: Date | null, status: string | null) => {
    const requests = getHolidayRequestsForDate(date);
    if (!status) return requests;
    return requests.filter(hr => hr.status.toLowerCase() === status.toLowerCase());
  };

  const getHolidayCountsForDate = (date: Date | null) => {
    const requests = getHolidayRequestsForDate(date);
    const total = requests.length;
    const pending = requests.filter(hr => hr.status.toLowerCase() === 'pending').length;
    const approved = requests.filter(hr => hr.status.toLowerCase() === 'approved').length;
    const denied = requests.filter(hr => hr.status.toLowerCase() === 'denied').length;
    const cancelled = requests.filter(hr => hr.status.toLowerCase() === 'cancelled').length;
    return { total, pending, approved, denied, cancelled };
  };

  const handleCountClick = (date: Date, status: string | null) => {
    setSelectedDate(date);
    setSelectedStatus(status);
    setShowDetailsModal(true);
  };

  const formatDateRange = (startDate: string, endDate: string) => {
    const start = new Date(startDate + 'T00:00:00');
    const end = new Date(endDate + 'T00:00:00');
    // Calculate days: if same date = 1 day, if different dates = difference + 1
    const days = Math.floor((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;
    const startFormatted = start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const endFormatted = end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    return `${startFormatted} - ${endFormatted} (${days} ${days === 1 ? 'day' : 'days'})`;
  };

  const getReturnDate = (endDate: string) => {
    const end = new Date(endDate);
    end.setDate(end.getDate() + 1);
    return end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'approved':
        return 'bg-green-50 text-green-700 border-green-200';
      case 'denied':
        return 'bg-red-50 text-red-700 border-red-200';
      case 'cancelled':
        return 'bg-neutral-50 text-neutral-700 border-neutral-200';
      case 'pending':
        return 'bg-orange-50 text-orange-700 border-orange-200';
      default:
        return 'bg-neutral-50 text-neutral-700 border-neutral-200';
    }
  };

  const [viewMode, setViewMode] = useState<'calendar' | 'table' | 'blocked'>('calendar');

  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const days = getDaysInMonth(currentMonth);

  const navigateMonth = (direction: 'prev' | 'next') => {
    setCurrentMonth(prev => {
      const newDate = new Date(prev);
      if (direction === 'prev') {
        newDate.setMonth(prev.getMonth() - 1);
      } else {
        newDate.setMonth(prev.getMonth() + 1);
      }
      return newDate;
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-neutral-500">Loading holiday requests...</div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-neutral-800">Holiday Admin</h1>
        <div className="flex items-center gap-3">
          <div className="inline-flex rounded-md border border-neutral-300 bg-white shadow-sm">
            <button
              type="button"
              onClick={() => setViewMode('calendar')}
              className={`px-3 py-1.5 text-sm font-medium rounded-l-md ${
                viewMode === 'calendar'
                  ? 'bg-blue-600 text-white'
                  : 'text-neutral-700 hover:bg-neutral-100'
              }`}
            >
              Calendar
            </button>
            <button
              type="button"
              onClick={() => setViewMode('table')}
              className={`px-3 py-1.5 text-sm font-medium border-l border-neutral-300 ${
                viewMode === 'table'
                  ? 'bg-blue-600 text-white'
                  : 'text-neutral-700 hover:bg-neutral-100'
              }`}
            >
              Table
            </button>
          </div>
          <button
            type="button"
            onClick={() => {
              setViewMode('blocked');
            }}
            className={`px-3 py-1.5 text-sm font-medium border rounded-md ${
              viewMode === 'blocked'
                ? 'border-red-500 bg-red-600 text-white'
                : 'border-red-300 text-red-700 hover:bg-red-50'
            }`}
          >
            Blocked Dates
          </button>
          <button
            onClick={() => {
              resetForm();
              setShowModal(true);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            <Plus className="h-4 w-4" />
            Add New Holiday Request
          </button>
        </div>
      </div>

      {/* Calendar */}
      {viewMode === 'calendar' && (
      <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-neutral-900">Holiday Calendar - Admin View</h1>
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigateMonth('prev')}
              className="px-2 py-1 text-neutral-600 hover:bg-neutral-100 rounded-md"
            >
              ←
            </button>
            <h2 className="text-lg font-semibold text-neutral-800">
              {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
            </h2>
            <button
              onClick={() => navigateMonth('next')}
              className="px-2 py-1 text-neutral-600 hover:bg-neutral-100 rounded-md"
            >
              →
            </button>
          </div>
        </div>

        <div className="grid grid-cols-7 gap-2 rounded-lg overflow-hidden">
          {/* Day headers */}
          {dayNames.map(day => (
            <div key={day} className="p-2 text-center text-sm font-medium text-neutral-500 bg-neutral-50 rounded">
              {day}
            </div>
          ))}

          {/* Calendar days */}
          {days.map((date, index) => {
            const counts = getHolidayCountsForDate(date);
            const isToday = date && date.toDateString() === new Date().toDateString();
            const isBlocked =
              !!date &&
              blockedDates.some(bd => {
                const bdStart = new Date(bd.startDate + 'T00:00:00');
                const bdEnd = new Date(bd.endDate + 'T00:00:00');
                return bdStart <= date && bdEnd >= date;
              });
            
            return (
              <div
                key={index}
                className={`min-h-[100px] p-1 border rounded ${
                  date ? (isBlocked ? 'bg-red-50 border-red-200' : 'bg-white border-black') : 'bg-neutral-50 border-black'
                } ${isToday ? 'ring-1 ring-blue-500' : ''}`}
              >
                {date && (
                  <>
                    <div className={`text-sm font-medium mb-1 ${isBlocked ? 'text-red-600' : isToday ? 'text-blue-600' : 'text-neutral-900'}`}>
                      {date.getDate()}
                    </div>
                    {isBlocked ? (
                      <div className="mt-1 text-xs text-red-600 flex flex-col gap-0.5">
                        <span>✕ Blocked</span>
                      </div>
                    ) : (
                      counts.total > 0 && (
                        <div className="space-y-0.5 text-xs">
                          <button
                            onClick={() => handleCountClick(date, null)}
                            className="flex items-center gap-1 text-blue-600 hover:text-blue-800 hover:underline w-full text-left"
                          >
                            <Users className="h-3 w-3" />
                            <span>{counts.total} total</span>
                          </button>
                          {counts.pending > 0 && (
                            <button
                              onClick={() => handleCountClick(date, 'pending')}
                              className="flex items-center gap-1 text-orange-600 hover:text-orange-800 hover:underline w-full text-left"
                            >
                              <Clock className="h-3 w-3" />
                              <span>{counts.pending} pending</span>
                            </button>
                          )}
                          {counts.approved > 0 && (
                            <button
                              onClick={() => handleCountClick(date, 'approved')}
                              className="flex items-center gap-1 text-green-600 hover:text-green-800 hover:underline w-full text-left"
                            >
                              <Check className="h-3 w-3" />
                              <span>{counts.approved} approved</span>
                            </button>
                          )}
                          {counts.denied > 0 && (
                            <button
                              onClick={() => handleCountClick(date, 'denied')}
                              className="flex items-center gap-1 text-red-600 hover:text-red-800 hover:underline w-full text-left"
                            >
                              <XCircle className="h-3 w-3" />
                              <span>{counts.denied} denied</span>
                            </button>
                          )}
                          {counts.cancelled > 0 && (
                            <button
                              onClick={() => handleCountClick(date, 'cancelled')}
                              className="flex items-center gap-1 text-neutral-600 hover:text-neutral-800 hover:underline w-full text-left"
                            >
                              <X className="h-3 w-3" />
                              <span>{counts.cancelled} cancelled</span>
                            </button>
                          )}
                        </div>
                      )
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>
      )}

      {/* Blocked Dates View */}
      {viewMode === 'blocked' && (
        <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-neutral-800">Blocked Dates</h2>
            <button
              type="button"
              onClick={() => {
                if (showBlockedDateForm && !editingBlockedDate) {
                  setShowBlockedDateForm(false);
                  setBlockedDateForm({ startDate: '', endDate: '', reason: '' });
                } else {
                  setEditingBlockedDate(null);
                  setBlockedDateForm({ startDate: '', endDate: '', reason: '' });
                  setShowBlockedDateForm(true);
                }
              }}
              className="flex items-center gap-2 px-3 py-1.5 bg-red-600 text-white rounded-md hover:bg-red-700 text-sm"
            >
              <Plus className="h-4 w-4" />
              {showBlockedDateForm && !editingBlockedDate ? 'Close Form' : 'Add Blocked Date'}
            </button>
          </div>

          {showBlockedDateForm && (
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                if (!blockedDateForm.startDate || !blockedDateForm.endDate) {
                  alert('Please select both start and end dates.');
                  return;
                }

                const start = new Date(blockedDateForm.startDate + 'T00:00:00');
                const end = new Date(blockedDateForm.endDate + 'T00:00:00');
                if (end < start) {
                  alert('End date must be after or equal to start date.');
                  return;
                }

                try {
                  if (editingBlockedDate) {
                    await blockedDateApi.update(editingBlockedDate.id, blockedDateForm);
                  } else {
                    await blockedDateApi.create(blockedDateForm);
                  }

                  const updated = await blockedDateApi.getAll();
                  setBlockedDates(updated);
                  setEditingBlockedDate(null);
                  setBlockedDateForm({ startDate: '', endDate: '', reason: '' });
                  setShowBlockedDateForm(false);
                } catch (error: any) {
                  console.error('Error saving blocked date:', error);
                  const errorMessage = error?.response?.data || error?.message || 'Failed to save blocked date.';
                  alert(errorMessage);
                }
              }}
              className="space-y-4 mb-6"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Start Date *</label>
                  <input
                    type="date"
                    required
                    value={blockedDateForm.startDate}
                    onChange={(e) => setBlockedDateForm({ ...blockedDateForm, startDate: e.target.value })}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">End Date *</label>
                  <input
                    type="date"
                    required
                    value={blockedDateForm.endDate}
                    onChange={(e) => setBlockedDateForm({ ...blockedDateForm, endDate: e.target.value })}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Reason <span className="text-xs text-neutral-400">(optional)</span>
                </label>
                <textarea
                  rows={2}
                  value={blockedDateForm.reason || ''}
                  onChange={(e) => setBlockedDateForm({ ...blockedDateForm, reason: e.target.value })}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setEditingBlockedDate(null);
                    setBlockedDateForm({ startDate: '', endDate: '', reason: '' });
                    setShowBlockedDateForm(false);
                  }}
                  className="px-4 py-2 border border-neutral-300 rounded-md text-neutral-700 hover:bg-neutral-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  {editingBlockedDate ? 'Save Changes' : 'Add Blocked Date'}
                </button>
              </div>
            </form>
          )}

          <div className="border border-neutral-200 rounded-lg overflow-hidden">
            <div className="bg-neutral-50 px-4 py-2 text-sm font-medium text-neutral-700">
              Existing Blocked Dates
            </div>
            {blockedDates.length === 0 ? (
              <div className="px-4 py-3 text-sm text-neutral-500">No blocked dates defined.</div>
            ) : (
              <table className="min-w-full divide-y divide-neutral-200 text-sm">
                <thead className="bg-neutral-50">
                  <tr>
                    <th className="px-4 py-2 text-left font-medium text-neutral-500">Start Date</th>
                    <th className="px-4 py-2 text-left font-medium text-neutral-500">End Date</th>
                    <th className="px-4 py-2 text-left font-medium text-neutral-500">Reason</th>
                    <th className="px-4 py-2 text-left font-medium text-neutral-500">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-200">
                  {blockedDates.map((bd) => {
                    const isSoftDeleted = softDeletedBlockedIds.includes(bd.id);
                    return (
                      <tr
                        key={bd.id}
                        className={isSoftDeleted ? 'line-through text-neutral-400 bg-neutral-50' : ''}
                      >
                        <td className="px-4 py-2">{new Date(bd.startDate).toLocaleDateString()}</td>
                        <td className="px-4 py-2">{new Date(bd.endDate).toLocaleDateString()}</td>
                        <td className="px-4 py-2">{bd.reason || 'N/A'}</td>
                        <td className="px-4 py-2">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => {
                                setEditingBlockedDate(bd);
                                setBlockedDateForm({
                                  startDate: bd.startDate,
                                  endDate: bd.endDate,
                                  reason: bd.reason || '',
                                });
                                setShowBlockedDateForm(true);
                              }}
                              className="text-blue-600 hover:text-blue-900"
                              title="Edit"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => {
                                setSoftDeletedBlockedIds(prev =>
                                  prev.includes(bd.id)
                                    ? prev.filter(id => id !== bd.id)
                                    : [...prev, bd.id]
                                );
                              }}
                              className="text-amber-600 hover:text-amber-900"
                              title={isSoftDeleted ? 'Undo soft delete' : 'Soft delete'}
                            >
                              <Archive className="h-4 w-4" />
                            </button>
                            <button
                              onClick={async () => {
                                if (!window.confirm('Are you sure you want to delete this blocked date?')) {
                                  return;
                                }
                                try {
                                  await blockedDateApi.delete(bd.id);
                                  const updated = await blockedDateApi.getAll();
                                  setBlockedDates(updated);
                                  setSoftDeletedBlockedIds(prev => prev.filter(id => id !== bd.id));
                                } catch (error: any) {
                                  console.error('Error deleting blocked date:', error);
                                  const errorMessage = error?.response?.data || error?.message || 'Failed to delete blocked date.';
                                  alert(errorMessage);
                                }
                              }}
                              className="text-red-600 hover:text-red-900"
                              title="Hard delete"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* Holiday Requests List */}
      {viewMode === 'table' && (
      <div className="bg-white rounded-lg shadow-sm border border-neutral-200">
        <div className="p-4 border-b border-neutral-200">
          <h2 className="text-lg font-semibold text-neutral-800">All Holiday Requests</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-neutral-200">
            <thead className="bg-neutral-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Employee</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Leave Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Start Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">End Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Days</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Reason</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Manager/Supervisor</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Venue/Department</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-neutral-200">
              {holidayRequests.length === 0 ? (
                <tr>
                  <td colSpan={10} className="px-6 py-4 text-center text-neutral-500">
                    No holiday requests found.
                  </td>
                </tr>
              ) : (
                holidayRequests.map((request) => {
                  const startDate = new Date(request.startDate);
                  const endDate = new Date(request.endDate);
                  const daysDiff = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1;
                  const isSoftDeleted = softDeletedIds.includes(request.id);
                  
                  return (
                    <tr
                      key={request.id}
                      className={isSoftDeleted ? 'line-through text-neutral-400 bg-neutral-50' : ''}
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {request.employee?.firstName} {request.employee?.lastName}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {request.leaveType?.name || 'N/A'}
                        {request.isHalfDay && request.halfDayType && (
                          <span className="ml-1 text-xs text-neutral-500">({request.halfDayType} Half Day)</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {startDate.toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {endDate.toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {request.totalDays || daysDiff} {request.totalDays || daysDiff === 1 ? 'day' : 'days'}
                        {request.isHalfDay && <span className="ml-1 text-xs text-neutral-500">(Half)</span>}
                      </td>
                      <td className="px-6 py-4 text-sm">
                        {request.reason || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(request.status)}`}>
                          {request.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {request.managerSupervisor || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {request.venueDepartment || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => {
                              const startDateStr = request.startDate.split('T')[0];
                              const endDateStr = request.endDate.split('T')[0];

                              setFormData({
                                employeeId: request.employeeId,
                                managerSupervisor: request.managerSupervisor,
                                venueDepartment: request.venueDepartment,
                                startDate: startDateStr,
                                endDate: endDateStr,
                                reason: request.reason || '',
                                status: request.status,
                              });
                              setIsEditing(true);
                              setEditingRequestId(request.id);
                              setShowModal(true);
                            }}
                            className="text-blue-600 hover:text-blue-900"
                            title="Edit"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => {
                              setSoftDeletedIds(prev =>
                                prev.includes(request.id)
                                  ? prev.filter(id => id !== request.id)
                                  : [...prev, request.id]
                              );
                            }}
                            className="text-amber-600 hover:text-amber-900"
                            title={isSoftDeleted ? 'Undo soft delete' : 'Soft delete'}
                          >
                            <Archive className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(request.id)}
                            className="text-red-600 hover:text-red-900"
                            title="Hard delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
      )}

      {/* Add / Edit Holiday Request Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
          <div className="bg-white rounded-lg p-6 w-full max-w-md my-8">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-neutral-800">
                {isEditing ? 'Edit Holiday Request' : 'Add New Holiday Request'}
              </h3>
              <button
                onClick={() => setShowModal(false)}
                className="text-neutral-400 hover:text-neutral-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Employee *</label>
                <select
                  required
                  value={formData.employeeId}
                  onChange={(e) => setFormData({ ...formData, employeeId: parseInt(e.target.value) })}
                  disabled={saving}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <option value={0}>Select Employee</option>
                  {employees.map((emp) => (
                    <option key={emp.id} value={emp.id}>
                      {emp.firstName} {emp.lastName}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Leave Type</label>
                <select
                  value={formData.leaveTypeId || ''}
                  onChange={(e) => setFormData({ ...formData, leaveTypeId: e.target.value ? parseInt(e.target.value) : undefined })}
                  disabled={saving}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <option value="">Select Leave Type (Optional)</option>
                  {leaveTypes.map((lt) => (
                    <option key={lt.leaveTypeId} value={lt.leaveTypeId}>
                      {lt.name} {lt.isStatutory ? '(Statutory)' : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Start Date *</label>
                <input
                  type="date"
                  required
                  value={formData.startDate}
                  onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                  disabled={saving}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">End Date *</label>
                <input
                  type="date"
                  required
                  value={formData.endDate}
                  onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                  disabled={saving}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                />
              </div>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.isHalfDay || false}
                    onChange={(e) => setFormData({ ...formData, isHalfDay: e.target.checked, halfDayType: e.target.checked ? formData.halfDayType || 'Morning' : undefined })}
                    disabled={saving}
                    className="rounded border-neutral-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50"
                  />
                  <span className="text-sm font-medium text-neutral-700">Half Day</span>
                </label>
                {formData.isHalfDay && (
                  <select
                    value={formData.halfDayType || 'Morning'}
                    onChange={(e) => setFormData({ ...formData, halfDayType: e.target.value })}
                    disabled={saving}
                    className="px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <option value="Morning">Morning</option>
                    <option value="Afternoon">Afternoon</option>
                  </select>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Status *</label>
                <select
                  required
                  value={formData.status || 'Pending'}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  disabled={saving}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <option value="Pending">Pending</option>
                  <option value="Approved">Approved</option>
                  <option value="Denied">Denied</option>
                  <option value="Cancelled">Cancelled</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Manager/Supervisor *</label>
                <input
                  type="text"
                  required
                  value={formData.managerSupervisor}
                  onChange={(e) => setFormData({ ...formData, managerSupervisor: e.target.value })}
                  disabled={saving}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  placeholder="Enter Manager/Supervisor name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Venue/Department *</label>
                <input
                  type="text"
                  required
                  value={formData.venueDepartment}
                  onChange={(e) => setFormData({ ...formData, venueDepartment: e.target.value })}
                  disabled={saving}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  placeholder="Enter Venue/Department name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Comments <span className="text-xs text-neutral-400">(optional)</span>
                </label>
                <textarea
                  rows={3}
                  value={formData.reason || ''}
                  onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                  disabled={saving}
                  placeholder="Enter any additional comments (optional)"
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                />
              </div>
              <div className="flex gap-3 justify-end pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                  disabled={saving}
                  className="px-4 py-2 border border-neutral-300 rounded-md text-neutral-700 hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? (isEditing ? 'Saving...' : 'Creating...') : isEditing ? 'Save Changes' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Holiday Details Modal */}
      {showDetailsModal && selectedDate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
          <div className="bg-white rounded-lg w-full max-w-2xl my-8 max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-neutral-200 px-6 py-4 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-neutral-900">
                Holiday Requests for {selectedDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
              </h3>
              <button
                onClick={() => {
                  setShowDetailsModal(false);
                  setSelectedDate(null);
                  setSelectedStatus(null);
                  setOpenActionsId(null);
                  setActionComment('');
                }}
                className="text-neutral-400 hover:text-neutral-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              {getHolidayRequestsForDateAndStatus(selectedDate, selectedStatus).length === 0 ? (
                <div className="text-center text-neutral-500 py-8">
                  No holiday requests found for this date{selectedStatus ? ` with status "${selectedStatus}"` : ''}.
                </div>
              ) : (
                getHolidayRequestsForDateAndStatus(selectedDate, selectedStatus).map((request) => {
                  const startDate = new Date(request.startDate);
                  const endDate = new Date(request.endDate);
                  const daysDiff = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1;
                  
                  return (
                    <div key={request.id} className="bg-white border border-neutral-200 rounded-lg p-4 shadow-sm">
                      <div className="flex justify-between items-start mb-3">
                        <h4 className="text-lg font-bold text-neutral-900">
                          {request.employee?.firstName} {request.employee?.lastName}
                        </h4>
                        <span className={`px-3 py-1 text-xs font-medium rounded-full flex items-center gap-1 ${getStatusColor(request.status)}`}>
                          {request.status.toLowerCase() === 'approved' && <Check className="h-3 w-3" />}
                          {request.status.toLowerCase() === 'pending' && <Clock className="h-3 w-3" />}
                          {request.status.toLowerCase() === 'denied' && <XCircle className="h-3 w-3" />}
                          {request.status}
                        </span>
                      </div>
                      <div className="text-sm text-neutral-600 mb-4">
                        {formatDateRange(request.startDate, request.endDate)}
                      </div>
                      <div className="grid grid-cols-2 gap-4 mb-4">
                        <div>
                          <label className="text-xs font-medium text-neutral-500 block mb-1">Reason</label>
                          <div className="text-sm text-neutral-900">{request.reason || 'N/A'}</div>
                        </div>
                        <div>
                          <label className="text-xs font-medium text-neutral-500 block mb-1">Department</label>
                          <div className="text-sm text-neutral-900">{request.venueDepartment || 'N/A'}</div>
                        </div>
                        <div>
                          <label className="text-xs font-medium text-neutral-500 block mb-1">Manager</label>
                          <div className="text-sm text-neutral-900">{request.managerSupervisor || 'N/A'}</div>
                        </div>
                        <div>
                          <label className="text-xs font-medium text-neutral-500 block mb-1">Return Date</label>
                          <div className="text-sm text-neutral-900">{getReturnDate(request.endDate)}</div>
                        </div>
                      </div>
                      <div className="flex justify-between items-start">
                        <button
                          onClick={() => {
                            // Prefill form with existing request data for editing
                            const startDateStr = request.startDate.split('T')[0];
                            const endDateStr = request.endDate.split('T')[0];

                            setFormData({
                              employeeId: request.employeeId,
                              managerSupervisor: request.managerSupervisor,
                              venueDepartment: request.venueDepartment,
                              startDate: startDateStr,
                              endDate: endDateStr,
                              leaveTypeId: request.leaveTypeId,
                              isHalfDay: request.isHalfDay || false,
                              halfDayType: request.halfDayType,
                              reason: request.reason || '',
                              status: request.status,
                            });
                            setIsEditing(true);
                            setEditingRequestId(request.id);
                            setShowDetailsModal(false);
                            setSelectedDate(null);
                            setSelectedStatus(null);
                            setOpenActionsId(null);
                            setActionComment('');
                            setShowModal(true);
                          }}
                          className="flex items-center gap-2 px-3 py-1.5 text-sm text-neutral-700 hover:bg-neutral-100 rounded-md border border-neutral-300"
                        >
                          <Edit className="h-4 w-4" />
                          Edit
                        </button>
                        {request.status.toLowerCase() === 'pending' && (
                          <button
                            onClick={() => {
                              if (openActionsId === request.id) {
                                setOpenActionsId(null);
                                setActionComment('');
                              } else {
                                setOpenActionsId(request.id);
                                setActionComment('');
                              }
                            }}
                            className="flex items-center gap-2 px-3 py-1.5 text-sm text-neutral-700 hover:bg-neutral-100 rounded-md border border-neutral-300"
                          >
                            Actions
                          </button>
                        )}
                      </div>
                      {request.status.toLowerCase() === 'pending' && openActionsId === request.id && (
                        <div className="mt-4 pt-4 border-t border-neutral-200 space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-neutral-700 mb-2">
                              Comments
                            </label>
                            <textarea
                              rows={3}
                              value={actionComment}
                              onChange={(e) => setActionComment(e.target.value)}
                              placeholder="Enter comments (required for denial)"
                              disabled={processingAction}
                              className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                            />
                          </div>
                          <div className="flex gap-3 justify-end">
                            <button
                              onClick={() => handleApproveWithComment(request.id)}
                              disabled={processingAction}
                              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <CheckCircle className="h-4 w-4" />
                              Approve
                            </button>
                            <button
                              onClick={() => handleDenyWithComment(request.id)}
                              disabled={processingAction}
                              className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <XCircle className="h-4 w-4" />
                              Deny
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HolidayAdmin;

